import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, FileText, Plus, Send, Warehouse } from 'lucide-react';
import { Button, Dialog, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import {
  isBasketLineReady,
  readyGroups,
  readyLineCount,
  type BasketGroup,
  type BasketLine,
} from '../model/basket.js';
import { BasketPanel } from './BasketPanel.js';

/**
 * SAVAT VA YUBORISH — bitta oyna, ikki qadam.
 *
 * Savat sahifaning yon ustunida turganda ikki muammo bor edi. Savat bo'sh
 * bo'lganda ustun bo'sh qolardi, to'lganda esa mahsulot qidiruvidan
 * yarmini yeb qo'yardi — holbuki ikkalasi bir vaqtda kerak emas: odam
 * yo qidiradi, yo yozganini ko'zdan kechiradi.
 *
 * Ikki qadam ATAYLAB bitta oynada. Ular bitta ishning ikki yarmi:
 * "nima so'rayman" va "kim uchun, qachonga". Ikkinchisini alohida
 * sahifaga chiqarish odamni savatdan uzib qo'yardi va u yozganini qayta
 * ko'rish uchun orqaga qaytishga majbur bo'lardi.
 */

export interface DocumentFields {
  purpose: string;
  throughPerson: string;
  neededBy: string;
  note: string;
}

export interface BasketDialogProps {
  open: boolean;
  onClose: () => void;
  lines: readonly BasketLine[];
  onPatch: (key: string, patch: Partial<BasketLine>) => void;
  onRemove: (key: string) => void;
  fields: DocumentFields;
  onFields: (patch: Partial<DocumentFields>) => void;
  onSave: (submit: boolean) => void;
  savingDraft?: boolean;
  savingSubmit?: boolean;
  error?: string | null;
}

export const BasketDialog = ({
  open,
  onClose,
  lines,
  onPatch,
  onRemove,
  fields,
  onFields,
  onSave,
  savingDraft = false,
  savingSubmit = false,
  error = null,
}: BasketDialogProps) => {
  const [stage, setStage] = useState<'basket' | 'send'>('basket');

  /* Har ochilishda savatdan boshlanadi: oldingi safar "yuborish" qadamida
     yopilgan bo'lsa, yangi mahsulot qo'shilgach ham o'sha yerda ochilib,
     qo'shilgan satr ko'rinmay qolardi. */
  useEffect(() => {
    if (open) setStage('basket');
  }, [open]);

  const groups = readyGroups(lines);
  const ready = readyLineCount(lines);
  /* Bo'sh «kim orqali» — ODATIY hol, lekin bitta harf yozilgani xato:
     server uni rad etadi va odam nima bo'lganini bilmay qolardi. */
  const throughValid = fields.throughPerson === '' || fields.throughPerson.trim().length >= 2;
  const canSave = fields.purpose.trim().length >= 3 && ready > 0 && throughValid;

  return (
    <Dialog
      open={open}
      /* `md` (560px) — `lg` (760px) emas. Savatdagi satr uch narsadan
         iborat: nom, miqdor va o'chirish. 760px da ular bir-biridan
         uzoqlashib, o'ng chekkadagi savat ikonkasi qaysi qatorga
         tegishli ekani ko'zdan qochardi. Ikkinchi qadamdagi forma ham
         shu kenglikda o'qishga qulay — satr uzunligi 75 belgidan
         oshmaydi. Kenglik ikki qadamda BIR XIL: qadam almashganda oyna
         "sakrab" kengaymasin. */
      size="md"
      onClose={onClose}
      title={t(stage === 'basket' ? 'requisition.basketTitle' : 'requisition.sendTitle')}
      /* Savat qadamida tana O'Z bo'shlig'ini bermaydi: guruh sarlavhasi
         va satr ajratuvchilari oynaning chetidan chetiga borishi kerak,
         aks holda yopishqoq sarlavha ostidan satrlar yon tomonlarda
         ko'rinib o'tib ketardi. Ichki bo'shliqni `BasketPanel` o'zi
         qo'yadi — sarlavha bilan bir xil 24px.

         `!` kerak: `cn` sinflarni faqat birlashtiradi (`tailwind-merge`
         emas) va `p-0` `px-6` ni o'zi yenga olmaydi. */
      bodyClassName={stage === 'basket' ? 'px-0! pb-0!' : undefined}
      footer={
        stage === 'basket' ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              <Plus size={14} aria-hidden />
              {t('requisition.addMore')}
            </Button>
            <Button disabled={ready === 0} onClick={() => setStage('send')}>
              {t('requisition.continue')}
              <ArrowRight size={14} aria-hidden />
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => setStage('basket')}>
              <ArrowLeft size={14} aria-hidden />
              {t('requisition.backToBasket')}
            </Button>
            <Button
              variant="secondary"
              loading={savingDraft}
              disabled={!canSave}
              onClick={() => onSave(false)}
            >
              {t('requisition.saveDraft')}
            </Button>
            <Button loading={savingSubmit} disabled={!canSave} onClick={() => onSave(true)}>
              <Send size={14} aria-hidden />
              {groups.length > 1
                ? t('requisition.submitCount', { count: groups.length })
                : t('requisition.submit')}
            </Button>
          </>
        )
      }
    >
      {stage === 'basket' ? (
        <BasketPanel lines={lines} onPatch={onPatch} onRemove={onRemove} />
      ) : (
        <div className="flex flex-col gap-5">
          <SendSummary groups={groups} />

          <div className="flex flex-col gap-3">
            <Input
              autoFocus
              label={t('requisition.fieldPurpose')}
              autoComplete="off"
              maxLength={500}
              value={fields.purpose}
              onChange={(event) => onFields({ purpose: event.target.value })}
            />
            {/* IXTIYORIY: mahsulotni ko'pincha ariza yozgan odamning o'zi
                oladi va o'shanda chiziq bo'sh qoladi. Majburiy qilish har
                talabnomada bekorga savol bo'lardi. */}
            <Input
              label={t('requisition.fieldThroughPerson')}
              hint={t('requisition.fieldThroughPersonHint')}
              autoComplete="off"
              maxLength={200}
              value={fields.throughPerson}
              onChange={(event) => onFields({ throughPerson: event.target.value })}
              error={throughValid ? undefined : t('requisition.errorThroughPerson')}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('requisition.fieldNeededBy')}
                type="date"
                value={fields.neededBy}
                onChange={(event) => onFields({ neededBy: event.target.value })}
              />
              <Input
                label={t('requisition.fieldNote')}
                autoComplete="off"
                maxLength={500}
                value={fields.note}
                onChange={(event) => onFields({ note: event.target.value })}
              />
            </div>
          </div>

          {error === null ? null : (
            <p
              role="alert"
              className={cn(
                'rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft',
                'px-3 py-2 text-sm text-danger',
              )}
            >
              {error}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
};

/**
 * NIMA YUBORILADI — yuborishdan oldingi oxirgi surat.
 *
 * Tugmada "2 ta talabnomani yuborish" deb yozilishi kifoya emas: odam
 * qaysi ombor qaysi satrlarni oladi degan savolga javobni ko'rmasa,
 * bo'linish tasodifiy tuyuladi. Har hujjat shu yerda alohida varaq
 * bo'lib turadi — bosishdan oldin nimaga imzo qo'yayotgani ko'rinadi.
 */
const SendSummary = ({ groups }: { groups: readonly BasketGroup[] }) => (
  <div className="flex flex-col gap-3">
    <p className="text-sm font-medium text-text">
      {groups.length === 1
        ? t('requisition.sendSummaryOne')
        : t('requisition.sendSummary', { count: groups.length })}
    </p>

    <ul className="grid gap-2.5 sm:grid-cols-2">
      {groups.map((group) => {
        const ready = group.lines.filter(isBasketLineReady);
        return (
          <li
            key={group.warehouseId}
            className={cn(
              'motion-slide-in flex flex-col gap-2 rounded-[var(--radius-md)]',
              'border border-border border-l-4 border-l-[var(--color-accent)]',
              'bg-surface-2 px-3 py-2.5',
            )}
          >
            <span className="flex items-center gap-2">
              <FileText size={14} aria-hidden className="shrink-0 text-accent" />
              <span className="text-xs font-semibold text-text">
                {t('requisition.docPreviewLines', { count: ready.length })}
              </span>
            </span>

            <span className="flex items-center gap-1.5 text-xs text-text-muted">
              <Warehouse size={12} aria-hidden className="shrink-0 text-text-faint" />
              <span className="min-w-0 truncate">{group.warehouseName}</span>
            </span>

            <ul className="flex flex-col gap-0.5">
              {ready.map((line) => (
                <li key={line.nomenclatureId} className="flex items-baseline gap-2 text-xs">
                  <span className="min-w-0 truncate text-text-muted">{line.name}</span>
                  <span className="tabular ml-auto shrink-0 text-text">
                    {line.qty} <span className="text-text-faint">{unitText(line.unit)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </li>
        );
      })}
    </ul>
  </div>
);
